import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { eq } from 'ember-truth-helpers';
import AdaptiveMenu from './adaptive-menu';
import Icon from './icon';
import FlagIcon from '~icons/lucide/flag';
import CheckIcon from '~icons/lucide/check';
import type { Store } from '@warp-drive/core';
import type Session from '../services/session';
import type IntlService from 'ember-intl/services/intl';
import {
  createFlag,
  type FlagReason,
  type FlagTargetType,
} from '../builders/flags';

type DropdownPlacement =
  | 'dropdown-start'
  | 'dropdown-end'
  | 'dropdown-center'
  | 'dropdown-top'
  | 'dropdown-bottom'
  | 'dropdown-left'
  | 'dropdown-right';

interface FlagButtonSignature {
  Args: {
    targetType: FlagTargetType;
    targetId: string | null;
    placement?: DropdownPlacement;
  };
}

export default class FlagButton extends Component<FlagButtonSignature> {
  @service declare store: Store;
  @service declare session: Session;
  @service declare intl: IntlService;

  @tracked selectedReason: FlagReason = 'incorrect';
  @tracked details = '';
  @tracked isSubmitting = false;
  @tracked submitted = false;
  @tracked errorMessage = '';

  get flagReasons(): { value: FlagReason; label: string }[] {
    return [
      { value: 'incorrect', label: this.intl.t('flags.reason.incorrect') },
      { value: 'spam', label: this.intl.t('flags.reason.spam') },
      {
        value: 'inappropriate',
        label: this.intl.t('flags.reason.inappropriate'),
      },
      { value: 'duplicate', label: this.intl.t('flags.reason.duplicate') },
      { value: 'other', label: this.intl.t('flags.reason.other') },
    ];
  }

  @action
  handleToggle(toggle: () => void) {
    if (!this.session.isAuthenticated) return;
    toggle();
  }

  @action
  selectReason(reason: FlagReason) {
    this.selectedReason = reason;
  }

  @action
  updateDetails(event: Event) {
    this.details = (event.target as HTMLTextAreaElement).value;
  }

  @action
  async handleSubmit(close: () => void) {
    this.isSubmitting = true;
    this.errorMessage = '';
    try {
      const targetId = this.args.targetId;
      if (!targetId) return;
      await this.store.request(
        createFlag({
          targetType: this.args.targetType,
          targetId,
          reason: this.selectedReason,
          details: this.details || undefined,
        })
      );
      this.submitted = true;
      close();
    } catch (error) {
      this.errorMessage =
        error instanceof Error ? error.message : 'Failed to submit';
    } finally {
      this.isSubmitting = false;
    }
  }

  @action
  resetForm() {
    this.selectedReason = 'incorrect';
    this.details = '';
    this.errorMessage = '';
  }

  <template>
    {{#if this.session.isAuthenticated}}
      {{#if this.submitted}}
        <span class="badge badge-success badge-sm gap-1" title="Flag submitted">
          <Icon @icon={{CheckIcon}} />
          Flagged
        </span>
      {{else}}
        <AdaptiveMenu
          @placement={{@placement}}
          @title="Report an issue"
          @contentClass="w-80 p-4"
          @onClose={{this.resetForm}}
        >
          <:trigger as |toggle|>
            <button
              type="button"
              class="btn btn-ghost btn-xs gap-1 opacity-60 hover:opacity-100"
              title="Report an issue"
              {{on "click" (fn this.handleToggle toggle)}}
            >
              <Icon @icon={{FlagIcon}} />
              Report
            </button>
          </:trigger>
          <:content as |close|>
            <h4 class="mb-2 text-sm font-semibold">Report an issue</h4>

            <div class="flex flex-col gap-1">
              {{#each this.flagReasons as |r|}}
                <label class="label cursor-pointer justify-start gap-2 py-1">
                  <input
                    type="radio"
                    name="flag-reason"
                    class="radio radio-sm"
                    checked={{eq this.selectedReason r.value}}
                    {{on "change" (fn this.selectReason r.value)}}
                  />
                  <span class="label-text text-sm">{{r.label}}</span>
                </label>
              {{/each}}
            </div>

            <textarea
              aria-label="Additional details"
              class="textarea textarea-bordered mt-2 w-full text-sm"
              rows="2"
              placeholder="Additional details (optional)"
              {{on "input" this.updateDetails}}
            ></textarea>

            {{#if this.errorMessage}}
              <p class="text-error mt-1 text-xs">{{this.errorMessage}}</p>
            {{/if}}

            <div class="mt-3 flex justify-end gap-2">
              <button
                type="button"
                class="btn btn-ghost btn-sm"
                {{on "click" close}}
              >
                Cancel
              </button>
              <button
                type="button"
                class="btn btn-error btn-sm"
                disabled={{this.isSubmitting}}
                {{on "click" (fn this.handleSubmit close)}}
              >
                {{if this.isSubmitting "Sending..." "Submit"}}
              </button>
            </div>
          </:content>
        </AdaptiveMenu>
      {{/if}}
    {{/if}}
  </template>
}
